import React, { useState } from 'react'
import {
  SettingsIcon,
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  TagIcon,
  InfoIcon,
  SaveIcon,
  CheckIcon,
  MailIcon,
  PhoneIcon,
  DollarSignIcon,
  HashIcon,
  FileTextIcon,
  UserIcon,
  GlobeIcon,
  SlackIcon,
  HomeIcon,
  FileIcon,
  CreditCardIcon,
  ReceiptIcon,
  FormInputIcon,
  BuildingIcon,
  CreditCard,
  ImageIcon,
  UsersIcon,
  ScrollTextIcon,
  BellIcon,
  ShieldIcon,
  LanguagesIcon,
  BadgeCheckIcon,
  ClockIcon,
  LockIcon,
  KeyIcon,
  ShoppingCartIcon,
  ShoppingBagIcon,
  TruckIcon,
  StoreIcon,
  PackageIcon,
  ArchiveIcon,
  PercentIcon,
} from 'lucide-react'
type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'number'
  | 'amount'
  | 'date'
  | 'url'
  | 'name'
interface PresetMetadataField {
  id: string
  name: string
  key: string
  type: FieldType
  required: boolean
  description: string
  defaultValue?: string
}
type SettingsTab =
  | 'company'
  | 'branding'
  | 'receipt'
  | 'payment-providers'
  | 'users'
  | 'terms'
  | 'general'
  | 'fields'
  | 'notifications'
  | 'security'
  | 'shop-settings'
  | 'order-settings'
export function Settings() {
  // Get tab from URL parameter if it exists
  const getInitialTab = (): SettingsTab => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const tabParam = urlParams.get('tab')
      if (
        tabParam &&
        [
          'company',
          'branding',
          'receipt',
          'payment-providers',
          'users',
          'terms',
          'general',
          'fields',
          'notifications',
          'security',
          'shop-settings',
          'order-settings',
        ].includes(tabParam)
      ) {
        return tabParam as SettingsTab
      }
    }
    return 'company'
  }
  const [activeTab, setActiveTab] = useState<SettingsTab>(getInitialTab())
  const [presetMetadataFields, setPresetMetadataFields] = useState<
    PresetMetadataField[]
  >([
    {
      id: '1',
      name: 'Customer Email',
      key: 'customer_email',
      type: 'email',
      required: true,
      description: "Customer's email address for receipts and communication",
    },
    {
      id: '2',
      name: 'Invoice Number',
      key: 'invoice_number',
      type: 'text',
      required: false,
      description: 'Reference invoice number for this payment',
    },
  ])
  const [defaultExpiryDays, setDefaultExpiryDays] = useState<number>(30)
  const [defaultRedirectUrl, setDefaultRedirectUrl] = useState<string>('')
  const [showCompanyAddress, setShowCompanyAddress] = useState<boolean>(true)
  const [receiptCCEmail, setReceiptCCEmail] = useState<string>('')
  const [savedSuccessfully, setSavedSuccessfully] = useState(false)
  // Company Information state
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: '9809674799',
    status: 'VERIFIED',
    businessName: 'GENIE BUSINESS',
    registrationNumber: 'C0010',
    gstNumber: '-',
    businessEmail: 'Sham2743@geniebusiness.lk',
    businessPhone: '9992310',
    language: 'English',
  })
  // Add payment methods state
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'card-payments', name: 'Card Payments', enabled: true },
    {
      id: 'multi-currency',
      name: 'Multi Currency Card Payments',
      enabled: true,
    },
    { id: 'bml-mobile', name: 'BML Mobile Pay', enabled: true },
    { id: 'alipay', name: 'Alipay', enabled: true },
    { id: 'wechatpay', name: 'WechatPay', enabled: true },
    { id: 'unionpay', name: 'UnionPay', enabled: true },
    { id: 'crypto', name: 'Cryptocurrency Payments', enabled: false },
  ])
  // Add E-Commerce settings state
  const [shopSettings, setShopSettings] = useState({
    enableMultipleShops: true,
    defaultCurrency: 'USD',
    enableInventoryTracking: true,
    lowStockThreshold: 5,
    allowBackorders: false,
    defaultShippingMethod: 'standard',
    enableProductReviews: true,
  })
  const [orderSettings, setOrderSettings] = useState({
    autoConfirmOrders: true,
    sendOrderConfirmationEmail: true,
    sendShippingNotifications: true,
    allowGuestCheckout: true,
    minimumOrderValue: 0,
    defaultOrderStatus: 'new',
    enableOrderTracking: true,
  })
  // Add shop selector state
  const [selectedShopId, setSelectedShopId] = useState<string>('1')
  const [showShopSelector, setShowShopSelector] = useState(false)
  // Mock shops data for settings
  const shopOptions = [
    { id: '1', name: 'Beach Shop', logo: '•', color: '#f8d7d7' },
    { id: '2', name: 'Island Shop', logo: '+', color: '#e1f5fe' },
    { id: '3', name: 'ABC', logo: 'A', color: '#e8f5e9' },
  ]
  const selectedShop =
    shopOptions.find((shop) => shop.id === selectedShopId) || shopOptions[0]
  // Add shop-specific settings
  const [shopInventorySettings, setShopInventorySettings] = useState({
    enableStockManagement: true,
    lowStockThreshold: 5,
    notifyOnLowStock: true,
    allowBackorders: false,
    hideOutOfStockProducts: false,
  })
  const [shopTaxSettings, setShopTaxSettings] = useState({
    enableTax: true,
    pricesIncludeTax: false,
    defaultTaxRate: 6,
    taxRates: [
      { id: '1', name: 'Standard Rate', rate: 6, isDefault: true },
      { id: '2', name: 'Reduced Rate', rate: 3, isDefault: false },
      { id: '3', name: 'Zero Rate', rate: 0, isDefault: false },
    ],
  })
  const [shopCustomerFields, setShopCustomerFields] = useState([
    {
      id: '1',
      name: 'Full Name',
      key: 'fullName',
      type: 'text',
      required: true,
      enabled: true,
    },
    {
      id: '2',
      name: 'Email',
      key: 'email',
      type: 'email',
      required: true,
      enabled: true,
    },
    {
      id: '3',
      name: 'Phone Number',
      key: 'phone',
      type: 'phone',
      required: true,
      enabled: true,
    },
    {
      id: '4',
      name: 'Address',
      key: 'address',
      type: 'text',
      required: true,
      enabled: true,
    },
    {
      id: '5',
      name: 'Additional Notes',
      key: 'notes',
      type: 'textarea',
      required: false,
      enabled: true,
    },
  ])
  const [shopFees, setShopFees] = useState([
    {
      id: '1',
      name: 'Delivery Fee',
      amount: 10,
      type: 'fixed',
      enabled: true,
      conditions: [{ type: 'orderValue', operator: 'lessThan', value: 50 }],
    },
    {
      id: '2',
      name: 'Service Charge',
      amount: 5,
      type: 'percentage',
      enabled: true,
      conditions: [],
    },
  ])
  const [shopNotificationSettings, setShopNotificationSettings] = useState({
    orderConfirmationMessage:
      'Thank you for your order! We will process it shortly.',
    notificationEmail: 'orders@beachshop.com',
    notificationPhone: '+9607123456',
    sendOrderNotifications: true,
    sendLowStockAlerts: true,
  })
  const [shopPaymentSettings, setShopPaymentSettings] = useState({
    payoutBankAccount: 'Maybank - 7701123456789',
    payoutFrequency: 'daily',
    minimumPayoutAmount: 100,
  })
  const togglePaymentMethod = (id: string) => {
    setPaymentMethods(
      paymentMethods.map((method) =>
        method.id === id ? { ...method, enabled: !method.enabled } : method,
      ),
    )
  }
  const fieldTypeIcons = {
    text: <FileTextIcon size={16} />,
    email: <MailIcon size={16} />,
    phone: <PhoneIcon size={16} />,
    number: <HashIcon size={16} />,
    amount: <DollarSignIcon size={16} />,
    date: <CalendarIcon size={16} />,
    url: <GlobeIcon size={16} />,
    name: <UserIcon size={16} />,
  }
  const addNewPresetField = () => {
    const newField: PresetMetadataField = {
      id: `field-${Date.now()}`,
      name: '',
      key: '',
      type: 'text',
      required: false,
      description: '',
    }
    setPresetMetadataFields([...presetMetadataFields, newField])
  }
  const updatePresetField = (
    id: string,
    field: Partial<PresetMetadataField>,
  ) => {
    setPresetMetadataFields(
      presetMetadataFields.map((item) =>
        item.id === id
          ? {
              ...item,
              ...field,
            }
          : item,
      ),
    )
  }
  const removePresetField = (id: string) => {
    setPresetMetadataFields(
      presetMetadataFields.filter((field) => field.id !== id),
    )
  }
  const handleSaveSettings = () => {
    // In a real application, this would save to a database or localStorage
    // For this demo, we'll just show a success message
    setSavedSuccessfully(true)
    // Clear the success message after 3 seconds
    setTimeout(() => {
      setSavedSuccessfully(false)
    }, 3000)
  }
  const renderCompanySettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <BuildingIcon size={18} className="mr-2 text-blue-600" />
        Company Information
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Some information cannot be edited once your company profile has
              been verified. Contact us if you need to update these.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Merchant ID
            </label>
            <input
              type="text"
              value={companyInfo.merchantId}
              readOnly
              className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <div className="flex items-center">
              <span className="inline-flex items-center px-2.5 py-2 rounded text-sm font-medium bg-green-100 text-green-800">
                <BadgeCheckIcon size={16} className="mr-1" />
                {companyInfo.status}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Registered Business Name
            </label>
            <input
              type="text"
              value={companyInfo.businessName}
              readOnly
              className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Registration Number
            </label>
            <input
              type="text"
              value={companyInfo.registrationNumber}
              readOnly
              className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GST Number
            </label>
            <div className="flex">
              <input
                type="text"
                value={companyInfo.gstNumber}
                onChange={(e) =>
                  setCompanyInfo({ ...companyInfo, gstNumber: e.target.value })
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <button className="ml-2 p-2 text-red-500 hover:text-red-700">
                <PencilIcon size={16} />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Email
            </label>
            <input
              type="email"
              value={companyInfo.businessEmail}
              onChange={(e) =>
                setCompanyInfo({
                  ...companyInfo,
                  businessEmail: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Phone
            </label>
            <input
              type="tel"
              value={companyInfo.businessPhone}
              onChange={(e) =>
                setCompanyInfo({
                  ...companyInfo,
                  businessPhone: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language Preference
            </label>
            <select
              value={companyInfo.language}
              onChange={(e) =>
                setCompanyInfo({ ...companyInfo, language: e.target.value })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="English">English</option>
              <option value="Dhivehi">Dhivehi</option>
            </select>
            <p className="mt-1 text-xs text-red-500">
              Personal Language preference will take precedence over the
              company's language setting.
            </p>
          </div>
        </div>
      </div>
    </>
  )
  const renderBrandingSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <ImageIcon size={18} className="mr-2 text-blue-600" />
        Branding
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Customize your payment page branding to match your company's
              visual identity. Your logo and colors will appear on payment
              pages, receipts, and other customer-facing interfaces.
            </p>
          </div>
        </div>
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <ImageIcon size={16} className="mr-2 text-blue-600" />
            Company Logo
          </h3>
          <div className="flex items-center space-x-6">
            <div className="w-32 h-32 bg-gray-100 border border-gray-300 rounded-md flex items-center justify-center">
              <img
                src="https://uploadthingy.s3.us-west-1.amazonaws.com/w9icsAoc3LreXhemq2jM7K/Logo_of_Bank_of_Maldives.svg"
                alt="Company Logo"
                className="max-w-full max-h-full p-2"
              />
            </div>
            <div className="space-y-3">
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                Upload New Logo
              </button>
              <p className="text-xs text-gray-500">
                Recommended size: 400 x 400 pixels (PNG or SVG format)
              </p>
            </div>
          </div>
        </div>
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <PaletteIcon size={16} className="mr-2 text-blue-600" />
            Brand Colors
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Color
              </label>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-md bg-[#1a2042] mr-3"></div>
                <input
                  type="text"
                  value="#1a2042"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Color
              </label>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-md bg-[#e54c3c] mr-3"></div>
                <input
                  type="text"
                  value="#e54c3c"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
  const renderGeneralSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <SettingsIcon size={18} className="mr-2 text-blue-600" />
        General Settings
      </h2>
      <div className="space-y-6">
        {/* Default Expiry Settings */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <CalendarIcon size={16} className="mr-2 text-blue-600" />
            Default Expiry Settings
          </h3>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
            <InfoIcon
              size={18}
              className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm text-blue-800">
                Set the default expiration period for all new payment links.
                This will be applied automatically when creating new payment
                links.
              </p>
              <p className="text-sm text-blue-800 mt-1">
                You can still customize the expiry date for individual payment
                links during creation.
              </p>
            </div>
          </div>
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default payment link expiry (in days)
            </label>
            <div className="flex items-center">
              <input
                type="number"
                min="1"
                max="365"
                value={defaultExpiryDays}
                onChange={(e) => setDefaultExpiryDays(parseInt(e.target.value))}
                className="block w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="ml-2 text-gray-700">days</span>
            </div>
            <div className="mt-4 flex space-x-3">
              <button
                onClick={() => setDefaultExpiryDays(7)}
                className={`px-3 py-1 rounded-md text-sm ${defaultExpiryDays === 7 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                7 days
              </button>
              <button
                onClick={() => setDefaultExpiryDays(30)}
                className={`px-3 py-1 rounded-md text-sm ${defaultExpiryDays === 30 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                30 days
              </button>
              <button
                onClick={() => setDefaultExpiryDays(90)}
                className={`px-3 py-1 rounded-md text-sm ${defaultExpiryDays === 90 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                90 days
              </button>
            </div>
          </div>
        </div>
        {/* Default Redirect URL */}
        <div>
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <GlobeIcon size={16} className="mr-2 text-blue-600" />
            Default Thank You Redirect
          </h3>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
            <InfoIcon
              size={18}
              className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm text-blue-800">
                Set a default URL where customers will be redirected after a
                successful payment.
              </p>
              <p className="text-sm text-blue-800 mt-1">
                You can override this for individual payment links during
                creation.
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default thank you page URL
            </label>
            <div className="flex items-center">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <GlobeIcon size={16} className="text-gray-400" />
                </div>
                <input
                  type="url"
                  value={defaultRedirectUrl}
                  onChange={(e) => setDefaultRedirectUrl(e.target.value)}
                  placeholder="https://yourdomain.com/thank-you"
                  className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Enter a full URL including https:// to redirect customers after
              payment
            </p>
          </div>
        </div>
      </div>
    </>
  )
  const renderReceiptSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <ReceiptIcon size={18} className="mr-2 text-blue-600" />
        Receipt Information
      </h2>
      <div className="space-y-6">
        {/* Company Address Settings */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <HomeIcon size={16} className="mr-2 text-blue-600" />
            Company Address on Receipts
          </h3>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
            <InfoIcon
              size={18}
              className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm text-blue-800">
                Choose whether to display your company address on payment
                receipts.
              </p>
            </div>
          </div>
          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showCompanyAddress}
                onChange={(e) => setShowCompanyAddress(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Show company address on receipts
              </span>
            </label>
            {showCompanyAddress && (
              <div className="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                <p className="text-sm text-gray-700 font-medium">
                  Current company address:
                </p>
                <p className="text-sm text-gray-600 mt-1">Maybank</p>
                <p className="text-sm text-gray-600">
                  11 Boduthakurufaanu Magu
                </p>
                <p className="text-sm text-gray-600">
                  Male', 20094, Republic of Maldives
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  To update your company address, please visit the Company
                  Profile section
                </p>
              </div>
            )}
          </div>
        </div>
        {/* Receipt CC Settings */}
        <div>
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <MailIcon size={16} className="mr-2 text-blue-600" />
            Receipt Email Notifications
          </h3>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
            <InfoIcon
              size={18}
              className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm text-blue-800">
                Set email addresses to receive copies of all payment receipts.
              </p>
              <p className="text-sm text-blue-800 mt-1">
                Separate multiple email addresses with commas.
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CC receipt to these email addresses
            </label>
            <div className="flex items-center">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MailIcon size={16} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={receiptCCEmail}
                  onChange={(e) => setReceiptCCEmail(e.target.value)}
                  placeholder="finance@example.com, accounting@example.com"
                  className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              These addresses will be CC'd on all receipt emails sent to
              customers
            </p>
          </div>
        </div>
      </div>
    </>
  )
  const renderPaymentProvidersSettings = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
        <h3 className="text-md font-medium text-gray-700 flex items-center">
          <WalletIcon size={16} className="mr-2 text-blue-600" />
          Available Payment Methods
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Enable or disable payment methods that your customers can use to pay
          you. Only enabled methods will appear on your payment forms.
        </p>
      </div>
      <div className="p-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Enable or disable payment methods that your customers can use to
              pay you. Only enabled payment methods will appear on your payment
              forms.
            </p>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="divide-y divide-gray-200">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="p-4 flex justify-between items-center"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mr-3">
                    {method.id === 'card-payments' && (
                      <CreditCardIcon size={20} className="text-blue-600" />
                    )}
                    {method.id === 'multi-currency' && (
                      <GlobeIcon size={20} className="text-blue-600" />
                    )}
                    {method.id === 'bml-mobile' && (
                      <SmartphoneIcon size={20} className="text-green-600" />
                    )}
                    {method.id === 'alipay' && (
                      <WalletIcon size={20} className="text-blue-500" />
                    )}
                    {method.id === 'wechatpay' && (
                      <WalletIcon size={20} className="text-green-500" />
                    )}
                    {method.id === 'unionpay' && (
                      <CreditCardIcon size={20} className="text-red-500" />
                    )}
                    {method.id === 'crypto' && (
                      <BitcoinIcon size={20} className="text-orange-500" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-800">
                      {method.name}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {method.id === 'card-payments' &&
                        'Accept credit and debit cards'}
                      {method.id === 'multi-currency' &&
                        'Accept cards in multiple currencies'}
                      {method.id === 'bml-mobile' &&
                        'Accept payments via Genie Mobile'}
                      {method.id === 'alipay' && 'Accept payments via Alipay'}
                      {method.id === 'wechatpay' &&
                        'Accept payments via WechatPay'}
                      {method.id === 'unionpay' && 'Accept UnionPay cards'}
                      {method.id === 'crypto' &&
                        'Accept Bitcoin and other cryptocurrencies'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={method.enabled}
                      onChange={() => togglePaymentMethod(method.id)}
                    />
                    <div
                      className={`w-10 h-5 rounded-full transition-colors ${method.enabled ? 'bg-blue-500' : 'bg-gray-300'} flex items-center p-0.5`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full bg-white transform transition-transform ${method.enabled ? 'translate-x-5' : ''}`}
                      />
                    </div>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
  const renderUsersSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <UsersIcon size={18} className="mr-2 text-blue-600" />
        Manage Users
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Manage user accounts and permissions for your payment portal. Add
              team members and control what they can access.
            </p>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-md font-medium text-gray-700">Team Members</h3>
          </div>
          <div className="divide-y divide-gray-200">
            <div className="p-4 flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#f8d7d7] rounded-full flex items-center justify-center text-[#e54c3c] font-medium mr-3">
                  SV
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-800">
                    Simon Verraest
                  </h4>
                  <p className="text-xs text-gray-500">
                    simon.verraest@bml.com.mv
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Admin
                </span>
                <button className="text-gray-400 hover:text-gray-600">
                  <SettingsIcon size={16} />
                </button>
              </div>
            </div>
            <div className="p-4 flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#e1f5fe] rounded-full flex items-center justify-center text-[#0288d1] font-medium mr-3">
                  JD
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-800">
                    Jane Doe
                  </h4>
                  <p className="text-xs text-gray-500">jane.doe@bml.com.mv</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Operator
                </span>
                <button className="text-gray-400 hover:text-gray-600">
                  <SettingsIcon size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <PlusIcon size={16} className="mr-2" />
          Invite Team Member
        </button>
      </div>
    </>
  )
  const renderTermsSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <ScrollTextIcon size={18} className="mr-2 text-blue-600" />
        Terms and Conditions
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Manage your terms and conditions, privacy policy, and other legal
              documents that will be displayed to customers.
            </p>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-md font-medium text-gray-700 mb-3">
            Payment Terms and Conditions
          </h3>
          <div className="border border-gray-300 rounded-md p-3 bg-gray-50 h-40 overflow-y-auto mb-4">
            <p className="text-sm text-gray-600">
              These are the standard terms and conditions for payments processed
              through Genie Business payment gateway. All customers must agree
              to these terms before completing their payment.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam in
              dui mauris. Vivamus hendrerit arcu sed erat molestie vehicula. Sed
              auctor neque eu tellus rhoncus ut eleifend nibh porttitor. Ut in
              nulla enim. Phasellus molestie magna non est bibendum non
              venenatis nisl tempor.
            </p>
          </div>
          <div className="flex justify-end">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Edit Terms
            </button>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-md font-medium text-gray-700 mb-3">
            Privacy Policy
          </h3>
          <div className="border border-gray-300 rounded-md p-3 bg-gray-50 h-40 overflow-y-auto mb-4">
            <p className="text-sm text-gray-600">
              This Privacy Policy describes how your personal information is
              collected, used, and shared when you make a payment through our
              service.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam in
              dui mauris. Vivamus hendrerit arcu sed erat molestie vehicula. Sed
              auctor neque eu tellus rhoncus ut eleifend nibh porttitor. Ut in
              nulla enim. Phasellus molestie magna non est bibendum non
              venenatis nisl tempor.
            </p>
          </div>
          <div className="flex justify-end">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              Edit Policy
            </button>
          </div>
        </div>
      </div>
    </>
  )
  const renderNotificationsSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <BellIcon size={18} className="mr-2 text-blue-600" />
        Notifications
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure how and when you receive notifications about payments,
              disputes, and system updates.
            </p>
          </div>
        </div>
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3">
            Email Notifications
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Payment Confirmations
                </p>
                <p className="text-xs text-gray-500">
                  Receive an email when a payment is completed
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked className="sr-only" />
                <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Failed Payments
                </p>
                <p className="text-xs text-gray-500">
                  Receive an email when a payment fails
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked className="sr-only" />
                <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Refunds</p>
                <p className="text-xs text-gray-500">
                  Receive an email when a refund is processed
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked className="sr-only" />
                <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  New dispute activity
                </p>
                <p className="text-xs text-gray-500">
                  Notify me each time there is a new dispute or update to
                  existing disputes
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only" />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Daily summary of dispute activity
                </p>
                <p className="text-xs text-gray-500">
                  Notify me at the end of each day about the changes on my
                  disputes that happened on that day
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only" />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full"></div>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-md font-medium text-gray-700 mb-3">
            SMS Notifications
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Large Payments
                </p>
                <p className="text-xs text-gray-500">
                  Receive an SMS for payments over $1,000
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only" />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Suspicious Activity
                </p>
                <p className="text-xs text-gray-500">
                  Receive an SMS for potentially fraudulent transactions
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked className="sr-only" />
                <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </label>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SMS Notification Phone Number
            </label>
            <input
              type="tel"
              value="+960 9992310"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
    </>
  )
  const renderSecuritySettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <ShieldIcon size={18} className="mr-2 text-blue-600" />
        Security
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure security settings to protect your account and payment
              processing.
            </p>
          </div>
        </div>
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <LockIcon size={16} className="mr-2 text-blue-600" />
            Two-Factor Authentication
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Enable Two-Factor Authentication
              </p>
              <p className="text-xs text-gray-500">
                Add an extra layer of security to your account
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked className="sr-only" />
              <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
              </div>
            </label>
          </div>
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm text-gray-700">
              Two-factor authentication is currently enabled for your account.
              You will receive a verification code via SMS when signing in from
              a new device.
            </p>
          </div>
        </div>
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <KeyIcon size={16} className="mr-2 text-blue-600" />
            API Keys
          </h3>
          <div className="space-y-4">
            <div className="p-4 border border-gray-200 rounded-md">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Production API Key
                </p>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
              </div>
              <div className="flex items-center">
                <input
                  type="password"
                  value="••••••••••••••••••••••••"
                  readOnly
                  className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <button className="ml-2 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                  Show
                </button>
                <button className="ml-2 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                  Regenerate
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Last used: 2 hours ago • Created: June 15, 2023
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-md">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium text-gray-700">
                  Test API Key
                </p>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Test Mode
                </span>
              </div>
              <div className="flex items-center">
                <input
                  type="password"
                  value="••••••••••••••••••••••••"
                  readOnly
                  className="block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <button className="ml-2 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                  Show
                </button>
                <button className="ml-2 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                  Regenerate
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Last used: 5 days ago • Created: June 15, 2023
              </p>
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <ShieldIcon size={16} className="mr-2 text-blue-600" />
            Fraud Protection
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Enable Fraud Detection
                </p>
                <p className="text-xs text-gray-500">
                  Automatically flag suspicious transactions
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked className="sr-only" />
                <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Block High-Risk Countries
                </p>
                <p className="text-xs text-gray-500">
                  Prevent payments from high-risk regions
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only" />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full"></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Require CVV for All Transactions
                </p>
                <p className="text-xs text-gray-500">
                  Always require card verification value
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked className="sr-only" />
                <div className="relative w-10 h-5 bg-blue-500 rounded-full">
                  <div className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5"></div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  )
  const renderInformationFieldsSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <FormInputIcon size={18} className="mr-2 text-blue-600" />
        Information Fields
      </h2>
      <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
        <InfoIcon
          size={18}
          className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
        />
        <div>
          <p className="text-sm text-blue-800">
            Preset information fields will be automatically added to all new
            payment links. These help you collect consistent information across
            all payments.
          </p>
          <p className="text-sm text-blue-800 mt-1">
            You can mark fields as required to ensure this data is always
            collected, or optional to give customers flexibility.
          </p>
        </div>
      </div>
      <div className="space-y-6">
        {presetMetadataFields.map((field) => (
          <div key={field.id} className="border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Name (Display Label)
                </label>
                <input
                  type="text"
                  value={field.name}
                  onChange={(e) =>
                    updatePresetField(field.id, {
                      name: e.target.value,
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Customer Email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Key (System Identifier)
                </label>
                <input
                  type="text"
                  value={field.key}
                  onChange={(e) =>
                    updatePresetField(field.id, {
                      key: e.target.value,
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., customer_email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Field Type
                </label>
                <select
                  value={field.type}
                  onChange={(e) =>
                    updatePresetField(field.id, {
                      type: e.target.value as FieldType,
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone Number</option>
                  <option value="number">Number</option>
                  <option value="amount">Amount</option>
                  <option value="date">Date</option>
                  <option value="url">URL</option>
                  <option value="name">Name</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Value (Optional)
                </label>
                <input
                  type="text"
                  value={field.defaultValue || ''}
                  onChange={(e) =>
                    updatePresetField(field.id, {
                      defaultValue: e.target.value,
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Leave blank for no default"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={field.description}
                  onChange={(e) =>
                    updatePresetField(field.id, {
                      description: e.target.value,
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Help text for this field"
                />
              </div>
              <div className="col-span-2 flex justify-between items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) =>
                      updatePresetField(field.id, {
                        required: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Required field
                  </span>
                </label>
                <button
                  onClick={() => removePresetField(field.id)}
                  className="text-red-600 hover:text-red-800 flex items-center"
                >
                  <TrashIcon size={16} className="mr-1" />
                  Remove Field
                </button>
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={addNewPresetField}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon size={16} className="mr-2" />
          Add New Information Field
        </button>
      </div>
    </>
  )
  const renderShopSelector = () => (
    <div className="relative">
      <button
        onClick={() => setShowShopSelector(!showShopSelector)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: selectedShop.color }}
        >
          <span className="text-red-600 text-xs">{selectedShop.logo}</span>
        </div>
        <span className="text-sm font-medium">{selectedShop.name}</span>
        <ChevronDownIcon size={16} className="text-gray-500" />
      </button>
      {showShopSelector && (
        <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 overflow-auto border border-gray-200">
          <div className="p-2">
            {shopOptions.map((shop) => (
              <button
                key={shop.id}
                onClick={() => {
                  setSelectedShopId(shop.id)
                  setShowShopSelector(false)
                }}
                className={`w-full flex items-center px-3 py-2 text-sm rounded-md ${
                  selectedShopId === shop.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center mr-2"
                  style={{ backgroundColor: shop.color }}
                >
                  <span className="text-red-600 text-xs">{shop.logo}</span>
                </div>
                <span>{shop.name}</span>
                {selectedShopId === shop.id && (
                  <CheckIcon size={16} className="ml-auto text-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
  const renderShopInventorySettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <PackageIcon size={18} className="mr-2 text-blue-600" />
        Inventory Management
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure how inventory is managed for {selectedShop.name}. These
              settings affect stock tracking and product availability.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Enable Stock Management
              </p>
              <p className="text-xs text-gray-500">
                Track inventory levels for products in this shop
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={shopInventorySettings.enableStockManagement}
                onChange={(e) =>
                  setShopInventorySettings({
                    ...shopInventorySettings,
                    enableStockManagement: e.target.checked,
                  })
                }
                className="sr-only"
              />
              <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                <div
                  className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopInventorySettings.enableStockManagement ? 'translate-x-5' : ''}`}
                />
              </div>
            </label>
          </div>
          {shopInventorySettings.enableStockManagement && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Low Stock Threshold
                </label>
                <input
                  type="number"
                  min="0"
                  value={shopInventorySettings.lowStockThreshold}
                  onChange={(e) =>
                    setShopInventorySettings({
                      ...shopInventorySettings,
                      lowStockThreshold: parseInt(e.target.value),
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Products with stock below this number will be marked as low
                  stock
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Notify on Low Stock
                  </p>
                  <p className="text-xs text-gray-500">
                    Receive notifications when products reach low stock
                    threshold
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shopInventorySettings.notifyOnLowStock}
                    onChange={(e) =>
                      setShopInventorySettings({
                        ...shopInventorySettings,
                        notifyOnLowStock: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                    <div
                      className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopInventorySettings.notifyOnLowStock ? 'translate-x-5' : ''}`}
                    />
                  </div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Allow Backorders
                  </p>
                  <p className="text-xs text-gray-500">
                    Allow customers to purchase products that are out of stock
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shopInventorySettings.allowBackorders}
                    onChange={(e) =>
                      setShopInventorySettings({
                        ...shopInventorySettings,
                        allowBackorders: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                    <div
                      className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopInventorySettings.allowBackorders ? 'translate-x-5' : ''}`}
                    />
                  </div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Hide Out of Stock Products
                  </p>
                  <p className="text-xs text-gray-500">
                    Automatically hide products when they are out of stock
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shopInventorySettings.hideOutOfStockProducts}
                    onChange={(e) =>
                      setShopInventorySettings({
                        ...shopInventorySettings,
                        hideOutOfStockProducts: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                    <div
                      className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopInventorySettings.hideOutOfStockProducts ? 'translate-x-5' : ''}`}
                    />
                  </div>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
  const renderShopTaxSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <PercentIcon size={18} className="mr-2 text-blue-600" />
        Tax Configuration
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure tax settings for {selectedShop.name}. These settings
              determine how taxes are calculated and displayed.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Enable Tax Calculation
              </p>
              <p className="text-xs text-gray-500">
                Calculate and apply taxes to orders
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={shopTaxSettings.enableTax}
                onChange={(e) =>
                  setShopTaxSettings({
                    ...shopTaxSettings,
                    enableTax: e.target.checked,
                  })
                }
                className="sr-only"
              />
              <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                <div
                  className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopTaxSettings.enableTax ? 'translate-x-5' : ''}`}
                />
              </div>
            </label>
          </div>
          {shopTaxSettings.enableTax && (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Prices Include Tax
                  </p>
                  <p className="text-xs text-gray-500">
                    Product prices already include tax
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shopTaxSettings.pricesIncludeTax}
                    onChange={(e) =>
                      setShopTaxSettings({
                        ...shopTaxSettings,
                        pricesIncludeTax: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                    <div
                      className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopTaxSettings.pricesIncludeTax ? 'translate-x-5' : ''}`}
                    />
                  </div>
                </label>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-md font-medium text-gray-700 mb-3">
                  Tax Rates
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden mb-4">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Rate (%)
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Default
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {shopTaxSettings.taxRates.map((taxRate) => (
                        <tr key={taxRate.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {taxRate.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {taxRate.rate}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            {taxRate.isDefault ? (
                              <CheckIcon
                                size={16}
                                className="mx-auto text-green-500"
                              />
                            ) : (
                              <button className="text-xs text-blue-600 hover:text-blue-800">
                                Set as default
                              </button>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button className="text-blue-600 hover:text-blue-800 mr-3">
                              Edit
                            </button>
                            <button className="text-red-600 hover:text-red-800">
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <PlusIcon size={16} className="mr-2" />
                  Add Tax Rate
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
  const renderShopCustomerFields = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <FormInputIcon size={18} className="mr-2 text-blue-600" />
        Customer Information Fields
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure the information fields that customers need to fill when
              placing an order in {selectedShop.name}.
            </p>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Field Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Required
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Enabled
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shopCustomerFields.map((field) => (
                <tr key={field.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {field.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {field.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => {
                        setShopCustomerFields(
                          shopCustomerFields.map((f) =>
                            f.id === field.id
                              ? { ...f, required: e.target.checked }
                              : f,
                          ),
                        )
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    <input
                      type="checkbox"
                      checked={field.enabled}
                      onChange={(e) => {
                        setShopCustomerFields(
                          shopCustomerFields.map((f) =>
                            f.id === field.id
                              ? { ...f, enabled: e.target.checked }
                              : f,
                          ),
                        )
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-800">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          <PlusIcon size={16} className="mr-2" />
          Add Custom Field
        </button>
      </div>
    </>
  )
  const renderShopFees = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <DollarSignIcon size={18} className="mr-2 text-blue-600" />
        Custom Fees & Service Charges
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure additional fees and service charges for{' '}
              {selectedShop.name}. These will be added to customer orders based
              on your rules.
            </p>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Fee Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Amount
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Conditions
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Enabled
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {shopFees.map((fee) => (
                <tr key={fee.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fee.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fee.type === 'fixed'
                      ? `$${fee.amount.toFixed(2)}`
                      : `${fee.amount}%`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {fee.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fee.conditions.length > 0 ? (
                      fee.conditions.map((condition, index) => (
                        <div key={index} className="text-xs">
                          {condition.type === 'orderValue' && (
                            <span>
                              Order value{' '}
                              {condition.operator === 'lessThan' ? '<' : '>'} $
                              {condition.value}
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">
                        Always applied
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    <input
                      type="checkbox"
                      checked={fee.enabled}
                      onChange={(e) => {
                        setShopFees(
                          shopFees.map((f) =>
                            f.id === fee.id
                              ? { ...f, enabled: e.target.checked }
                              : f,
                          ),
                        )
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-blue-600 hover:text-blue-800 mr-3">
                      Edit
                    </button>
                    <button className="text-red-600 hover:text-red-800">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          <PlusIcon size={16} className="mr-2" />
          Add Fee
        </button>
      </div>
    </>
  )
  const renderShopPaymentSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <BankIcon size={18} className="mr-2 text-blue-600" />
        Payment & Payout Settings
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure payment and payout settings for {selectedShop.name}.
              These settings determine how you receive payments from sales.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payout Bank Account
            </label>
            <input
              type="text"
              value={shopPaymentSettings.payoutBankAccount}
              onChange={(e) =>
                setShopPaymentSettings({
                  ...shopPaymentSettings,
                  payoutBankAccount: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter the bank account where you want to receive your payouts
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payout Frequency
            </label>
            <select
              value={shopPaymentSettings.payoutFrequency}
              onChange={(e) =>
                setShopPaymentSettings({
                  ...shopPaymentSettings,
                  payoutFrequency: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Payout Amount
            </label>
            <div className="flex items-center">
              <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                $
              </span>
              <input
                type="number"
                min="0"
                value={shopPaymentSettings.minimumPayoutAmount}
                onChange={(e) =>
                  setShopPaymentSettings({
                    ...shopPaymentSettings,
                    minimumPayoutAmount: parseInt(e.target.value),
                  })
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Payouts will only be processed when the balance exceeds this
              amount
            </p>
          </div>
        </div>
      </div>
    </>
  )
  const renderShopNotificationSettings = () => (
    <>
      <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
        <BellIcon size={18} className="mr-2 text-blue-600" />
        Order Notifications
      </h2>
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
          <InfoIcon
            size={18}
            className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm text-blue-800">
              Configure order notifications and confirmation messages for{' '}
              {selectedShop.name}.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Confirmation Message
            </label>
            <textarea
              rows={3}
              value={shopNotificationSettings.orderConfirmationMessage}
              onChange={(e) =>
                setShopNotificationSettings({
                  ...shopNotificationSettings,
                  orderConfirmationMessage: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Thank you for your order!"
            />
            <p className="mt-1 text-xs text-gray-500">
              This message will be displayed to customers after they complete an
              order
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notification Email
            </label>
            <input
              type="email"
              value={shopNotificationSettings.notificationEmail}
              onChange={(e) =>
                setShopNotificationSettings({
                  ...shopNotificationSettings,
                  notificationEmail: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="orders@example.com"
            />
            <p className="mt-1 text-xs text-gray-500">
              Email address where order notifications will be sent
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notification Phone Number
            </label>
            <input
              type="tel"
              value={shopNotificationSettings.notificationPhone}
              onChange={(e) =>
                setShopNotificationSettings({
                  ...shopNotificationSettings,
                  notificationPhone: e.target.value,
                })
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="+1234567890"
            />
            <p className="mt-1 text-xs text-gray-500">
              Phone number where order notifications will be sent
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Send Order Notifications
              </p>
              <p className="text-xs text-gray-500">
                Receive notifications for new orders
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={shopNotificationSettings.sendOrderNotifications}
                onChange={(e) =>
                  setShopNotificationSettings({
                    ...shopNotificationSettings,
                    sendOrderNotifications: e.target.checked,
                  })
                }
                className="sr-only"
              />
              <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                <div
                  className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopNotificationSettings.sendOrderNotifications ? 'translate-x-5' : ''}`}
                />
              </div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Send Low Stock Alerts
              </p>
              <p className="text-xs text-gray-500">
                Receive notifications when products reach low stock levels
              </p>
            </div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={shopNotificationSettings.sendLowStockAlerts}
                onChange={(e) =>
                  setShopNotificationSettings({
                    ...shopNotificationSettings,
                    sendLowStockAlerts: e.target.checked,
                  })
                }
                className="sr-only"
              />
              <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                <div
                  className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${shopNotificationSettings.sendLowStockAlerts ? 'translate-x-5' : ''}`}
                />
              </div>
            </label>
          </div>
        </div>
      </div>
    </>
  )
  const renderShopSettings = () => (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-800 flex items-center">
          <ShoppingBagIcon size={18} className="mr-2 text-blue-600" />
          Shop Settings
        </h2>
        {renderShopSelector()}
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
        <div className="flex">
          <InfoIcon className="h-5 w-5 text-yellow-500 mr-3 flex-shrink-0" />
          <p className="text-sm text-yellow-700">
            These settings apply specifically to{' '}
            <strong>{selectedShop.name}</strong>. Switch shops using the
            selector above to configure settings for other shops.
          </p>
        </div>
      </div>
      <div className="space-y-8">
        {/* Shop Inventory Management */}
        <div className="border-b border-gray-200 pb-6">
          {renderShopInventorySettings()}
        </div>
        {/* Shop Tax Configuration */}
        <div className="border-b border-gray-200 pb-6">
          {renderShopTaxSettings()}
        </div>
        {/* Shop Customer Fields */}
        <div className="border-b border-gray-200 pb-6">
          {renderShopCustomerFields()}
        </div>
        {/* Shop Fees */}
        <div className="border-b border-gray-200 pb-6">{renderShopFees()}</div>
        {/* Shop Payment Settings */}
        <div className="border-b border-gray-200 pb-6">
          {renderShopPaymentSettings()}
        </div>
        {/* Shop Notification Settings */}
        <div>{renderShopNotificationSettings()}</div>
      </div>
    </>
  )
  const renderOrderSettings = () => (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-800 flex items-center">
          <ShoppingCartIcon size={18} className="mr-2 text-blue-600" />
          Order Settings
        </h2>
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-md p-4 mb-6 flex items-start">
        <InfoIcon
          size={18}
          className="text-blue-600 mr-2 mt-0.5 flex-shrink-0"
        />
        <div>
          <p className="text-sm text-blue-800">
            Configure how orders are processed and managed across all shops.
            These settings apply globally to all shops unless overridden in
            shop-specific settings.
          </p>
        </div>
      </div>
      <div className="space-y-8">
        {/* Order Processing Settings */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <ShoppingCartIcon size={16} className="mr-2 text-blue-600" />
            Order Processing
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Auto-Confirm Orders
                </p>
                <p className="text-xs text-gray-500">
                  Automatically confirm orders when payment is received
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={orderSettings.autoConfirmOrders}
                  onChange={(e) =>
                    setOrderSettings({
                      ...orderSettings,
                      autoConfirmOrders: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div
                    className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${orderSettings.autoConfirmOrders ? 'translate-x-5' : ''}`}
                  />
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Allow Guest Checkout
                </p>
                <p className="text-xs text-gray-500">
                  Allow customers to place orders without creating an account
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={orderSettings.allowGuestCheckout}
                  onChange={(e) =>
                    setOrderSettings({
                      ...orderSettings,
                      allowGuestCheckout: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div
                    className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${orderSettings.allowGuestCheckout ? 'translate-x-5' : ''}`}
                  />
                </div>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Order Value
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  value={orderSettings.minimumOrderValue}
                  onChange={(e) =>
                    setOrderSettings({
                      ...orderSettings,
                      minimumOrderValue: parseInt(e.target.value),
                    })
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-r-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Set to 0 for no minimum order value
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Order Status
              </label>
              <select
                value={orderSettings.defaultOrderStatus}
                onChange={(e) =>
                  setOrderSettings({
                    ...orderSettings,
                    defaultOrderStatus: e.target.value,
                  })
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="new">New</option>
                <option value="processing">Processing</option>
                <option value="on-hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Status assigned to new orders by default
              </p>
            </div>
          </div>
        </div>
        {/* Order Notifications */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <BellIcon size={16} className="mr-2 text-blue-600" />
            Order Notifications
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Send Order Confirmation Email
                </p>
                <p className="text-xs text-gray-500">
                  Send an email to customers when their order is confirmed
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={orderSettings.sendOrderConfirmationEmail}
                  onChange={(e) =>
                    setOrderSettings({
                      ...orderSettings,
                      sendOrderConfirmationEmail: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div
                    className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${orderSettings.sendOrderConfirmationEmail ? 'translate-x-5' : ''}`}
                  />
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Send Shipping Notifications
                </p>
                <p className="text-xs text-gray-500">
                  Notify customers when their order ships
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={orderSettings.sendShippingNotifications}
                  onChange={(e) =>
                    setOrderSettings({
                      ...orderSettings,
                      sendShippingNotifications: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div
                    className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${orderSettings.sendShippingNotifications ? 'translate-x-5' : ''}`}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
        {/* Order Tracking */}
        <div>
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center">
            <TruckIcon size={16} className="mr-2 text-blue-600" />
            Order Tracking
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Enable Order Tracking
                </p>
                <p className="text-xs text-gray-500">
                  Allow customers to track their order status
                </p>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={orderSettings.enableOrderTracking}
                  onChange={(e) =>
                    setOrderSettings({
                      ...orderSettings,
                      enableOrderTracking: e.target.checked,
                    })
                  }
                  className="sr-only"
                />
                <div className="relative w-10 h-5 bg-gray-300 rounded-full">
                  <div
                    className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${orderSettings.enableOrderTracking ? 'translate-x-5' : ''}`}
                  />
                </div>
              </label>
            </div>
            {orderSettings.enableOrderTracking && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-700">
                  Order tracking is enabled. Customers will receive a tracking
                  link in their order confirmation email where they can check
                  their order status.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings()
      case 'branding':
        return renderBrandingSettings()
      case 'general':
        return renderGeneralSettings()
      case 'receipt':
        return renderReceiptSettings()
      case 'payment-providers':
        return renderPaymentProvidersSettings()
      case 'users':
        return renderUsersSettings()
      case 'terms':
        return renderTermsSettings()
      case 'fields':
        return renderInformationFieldsSettings()
      case 'notifications':
        return renderNotificationsSettings()
      case 'security':
        return renderSecuritySettings()
      case 'shop-settings':
        return renderShopSettings()
      case 'order-settings':
        return renderOrderSettings()
      default:
        return renderCompanySettings()
    }
  }
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <SettingsIcon size={24} className="mr-2 text-blue-600" />
          Settings
        </h1>
        <button
          onClick={handleSaveSettings}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors"
        >
          <SaveIcon size={16} className="mr-2" />
          Save Settings
        </button>
      </div>
      {savedSuccessfully && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center">
          <CheckIcon size={20} className="mr-2 text-green-500" />
          Settings saved successfully
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left sidebar menu */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Account
              </h3>
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('company')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'company' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <BuildingIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'company' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Company
                </button>
                <button
                  onClick={() => setActiveTab('branding')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'branding' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <ImageIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'branding' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Branding
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'users' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <UsersIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'users' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Manage Users
                </button>
              </nav>
            </div>
            <div className="border-t border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Payment Settings
              </h3>
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'general' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <SettingsIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'general' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  General
                </button>
                <button
                  onClick={() => setActiveTab('payment-providers')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'payment-providers' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <CreditCardIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'payment-providers' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Payment Providers
                </button>
                <button
                  onClick={() => setActiveTab('receipt')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'receipt' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <ReceiptIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'receipt' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Receipt Information
                </button>
                <button
                  onClick={() => setActiveTab('fields')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'fields' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <FormInputIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'fields' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Information Fields
                </button>
              </nav>
            </div>
            <div className="border-t border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                E-Commerce
              </h3>
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('shop-settings')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'shop-settings' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <ShoppingBagIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'shop-settings' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Shop Settings
                </button>
                <button
                  onClick={() => setActiveTab('order-settings')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'order-settings' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <ShoppingCartIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'order-settings' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Order Settings
                </button>
              </nav>
            </div>
            <div className="border-t border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                System
              </h3>
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'notifications' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <BellIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'notifications' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Notifications
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'security' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <ShieldIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'security' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Security
                </button>
                <button
                  onClick={() => setActiveTab('terms')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'terms' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <ScrollTextIcon
                    size={18}
                    className={`mr-3 ${activeTab === 'terms' ? 'text-blue-600' : 'text-gray-500'}`}
                  />
                  Terms and Conditions
                </button>
              </nav>
            </div>
          </div>
        </div>
        {/* Content area */}
        <div className="md:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {renderActiveTabContent()}
          </div>
        </div>
      </div>
    </div>
  )
}
// Missing icons that need to be defined
const PencilIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
)
const PaletteIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="13.5" cy="6.5" r=".5" />
    <circle cx="17.5" cy="10.5" r=".5" />
    <circle cx="8.5" cy="7.5" r=".5" />
    <circle cx="6.5" cy="12.5" r=".5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
)
const LanguagesIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m5 8 6 6" />
    <path d="m4 14 6-6 2-3" />
    <path d="M2 5h12" />
    <path d="M7 2h1" />
    <path d="m22 22-5-10-5 10" />
    <path d="M14 18h6" />
  </svg>
)
const SmartphoneIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
)
const BitcoinIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M11.767 19.089c4.924.868 6.14-6.025 1.216-6.894m-1.216 6.894L5.86 18.047m5.908 1.042-.347 1.97m1.563-8.864c4.924.869 6.14-6.025 1.215-6.893m-1.215 6.893-3.94-.694m5.155-6.2L8.29 4.26m5.908 1.042.348-1.97M7.48 20.364l3.126-17.727" />
  </svg>
)
const WalletIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
  </svg>
)
const ChevronDownIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m18 15-6-6-6 6" />
  </svg>
)
const BankIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="9" width="18" height="12" rx="1" />
    <path d="M8 9V5c0-1 1-2 2-2h4c1 0 2 1 2 2v4" />
    <line x1="12" y1="15" x2="12" y2="15.01" />
  </svg>
)
